<template>
  <div class="fill-height d-flex flex-column selection-page-wrapper">
    <v-expand-transition>
      <div
        v-if="showHint"
        class="info-container"
        data-testid="station-table-info-text"
      >
        <v-icon color="primary">
          mdi-alert-circle-outline
        </v-icon>
        <div class="ml-4">
          {{ t('station_table.info_text') }}
        </div>
        <v-spacer />
        <v-btn
          icon
          size="32"
          variant="text"
          data-testid="remove-station-table-info-text"
          @click="removeHint"
        >
          <v-icon color="scrim">
            mdi-close-circle
          </v-icon>
        </v-btn>
      </div>
    </v-expand-transition>
    <div class="d-flex">
      <v-btn
        :class="filterButtonClass"
        depressed
        variant="outlined"
        data-testid="filter-drawer-toggle-show"
        :color="showFilter ? 'primary' : ''"
        @click="showFilter = !showFilter"
      >
        <v-icon class="mr-4">
          mdi-filter
        </v-icon>
        {{ t('station_table.filter') }}
      </v-btn>
      <v-tooltip
        :text="t('station_table.chips.matching_stations_tooltip')"
        data-testid="tooltip-matching-stations"
        bottom
        :open-delay="200"
        max-width="400"
        style="z-index: 99"
      >
        <template #activator="{ props }">
          <v-chip
            v-bind="props"
            class="ml-4"
            data-testid="filter-chip-station-count"
            :v-if="stationStore.filtersAreSet"
          >
            <v-icon
              data-testid="tooltip-matching-stations-icon"
              left
            >
              mdi-filter
            </v-icon>
            {{
              stationStore.getMatchingStationCount +
                '/' +
                stationStore.getAllStationCount
            }}
          </v-chip>
        </template>
      </v-tooltip>
      <v-chip
        v-for="filterName of Object.keys(stationStore.filters)"
        :key="filterName"
        class="ml-4"
        :data-testid="`filter-chip-item-${filterName}`"
        closable
        @click:close="removeFilter(filterName)"
      >
        <v-icon left>
          mdi-filter
        </v-icon>
        {{ t(`station_table.chips.${filterName}`) }}
      </v-chip>
      <v-spacer data-testid="station-selection-page-spacer" />
      <v-btn
        class="zamg-button zamg-button-outline-divider text-uppercase"
        depressed
        variant="outlined"
        data-testid="station-table-map-btn"
        @click="mapButtonPressed()"
      >
        <v-icon class="mr-4">
          mdi-map
        </v-icon>
        {{ t('station_table.map') }}
      </v-btn>
    </div>
    <div class="mt-4 flex-grow-1 position-relative">
      <filter-drawer
        v-model:show="showFilter"
        data-testid="filter-drawer"
        class="filter-drawer"
        @close="showFilter = false"
      />
      <station-table
        class="station-table"
        :selected="stationStore.selectedStations"
        :items="stationStore.getAvailableStations"
        :is-loading="stationStore.isLoading"
        :active-filters="stationStore.filters"
        @select="handleStationSelect"
      />
      <Transition
        name="slide-from-right"
      >
        <station-map
          v-show="readyToRenderMap && showMap"
          class="station-map"
          data-testid="station-map"
          :visible="showMap"
          @hide="mapButtonPressed"
        />
      </Transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Filters, Station } from '@/stores/station/station-model';
import StationStore from '@/stores/station/station-module';
import StationMap from '@/components/station/stationMap/StationMap.vue';
import FilterDrawer from '@/components/common/FilterDrawer.vue';
import StationTable from '@/components/station/StationTable.vue';
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: 'StationSelectionPage',
  components: {
    StationTable,
    FilterDrawer,
    StationMap,
  },
  setup() {
    const { t } = useI18n();

    const stationStore = StationStore;
    const showMap = ref(false);
    const readyToRenderMap = ref(false);
    const showFilter = ref(false);
    const showHint = ref(true);
    const viewportHeight = ref(window.innerHeight);

    const onResize = () => {
      viewportHeight.value = window.innerHeight;
    };

    onMounted(() => {
      fetchStations();
      readyToRenderMap.value = true;
      showMap.value = true;
      window.addEventListener('resize', onResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize);
    });

    const filterButtonClass = computed<string>(() => {
      return (
        'zamg-button text-uppercase' +
        (stationStore.filtersAreSet && !showMap.value
          ? ' zamg-button-outline-primary active-light'
          : ' zamg-button-outline-divider')
      );
    });

    function handleStationSelect(stations: Station[]) {
      stationStore.setSelectedStations(stations);
    }

    function removeHint() {
      showHint.value = false;
    }

    const removeFilter = async (filterName: keyof Filters): Promise<void> => {
      stationStore.putFilter({ [filterName]: undefined });
      await fetchStations();
    };

    const fetchStations = async (): Promise<void> => {
      await stationStore.fetchFilteredStations();
    };

    function resetStationsAndFilters() {
      // called from "abort" button in stepper
      resetStations();
      stationStore.updateFilters({});
      stationStore.fetchFilteredStations();
    }

    function resetStations() {
      stationStore.setSelectedStations([]);
    }

    function mapButtonPressed() {
      showMap.value = !showMap.value;
    }

    return {
      t,
      stationStore,
      filterButtonClass,
      handleStationSelect,
      removeHint,
      removeFilter,
      resetStationsAndFilters,
      mapButtonPressed,
      showFilter,
      showHint,
      showMap,
      readyToRenderMap, // shows map
    }
  }
});
</script>

<style scoped>
.selection-page-wrapper {
  min-height: 460px;
}
.station-table {
  position: absolute;
  width: 100%;
  height: 100%;
}

.position-relative {
  position: relative;
}

.station-map {
  width: 50%;
  height: 100%;
  float: right;
  z-index: 9;
}

.info-container {
  width: calc(100% + 16px + 16px);
  display: flex;
  height: 40px;
  align-items: center;
  background: rgb(var(--v-theme-container));
  margin: -16px -16px 16px -16px;
  padding: 0 16px 0 16px;
  border-top: 1px solid rgb(var(--v-theme-primary));
  border-bottom: 1px solid rgb(var(--v-theme-primary));
}

.v-chip {
  background: rgb(var(--v-theme-secondary));
  color: rgb(var(--v-theme-primary));
}

.slide-from-right-enter-active {
  animation: slide-from-right 0.3s;
}
.slide-from-right-leave-active {
  animation: slide-from-right 0.3s reverse;
}
@keyframes slide-from-right {
  from {
    transform: translateX(calc(100% + 16px));
  }
  to {
    transform: translateX(0);
  }
}
</style>
